import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class ProductDeliveryStatus extends HTMLElement {
  /** @type {AbortController | undefined} */
  #cartRefreshController;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateStatus);
    document.addEventListener(ThemeEvents.cartUpdate, this.updateStatusFromCart);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateStatus);
    document.removeEventListener(ThemeEvents.cartUpdate, this.updateStatusFromCart);
    this.#cartRefreshController?.abort();
  }

  /**
   * Updates the delivery status when the selected variant changes.
   * @param {VariantUpdateEvent} event
   */
  updateStatus = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newStatus = event.detail.data.html.querySelector('product-delivery-status');

    if (!newStatus) return;

    morph(this, newStatus, { childrenOnly: false });
  };

  /**
   * Refreshes the delivery status after cart changes so the free-shipping
   * threshold reflects the current cart subtotal.
   * @param {CustomEvent} event
   */
  updateStatusFromCart = (event) => {
    if (event.detail?.data?.didError || this.dataset.freeShippingEnabled !== 'true') return;

    this.#cartRefreshController?.abort();
    const refreshController = new AbortController();
    this.#cartRefreshController = refreshController;

    fetch(window.location.href, {
      signal: refreshController.signal,
      cache: 'no-store',
      headers: {
        Accept: 'text/html',
      },
    })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newStatus =
          html.querySelector(`product-delivery-status[data-product-id="${this.dataset.productId}"]`) ??
          html.querySelector('product-delivery-status');

        if (!newStatus) return;

        morph(this, newStatus, { childrenOnly: false });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        if (this.#cartRefreshController === refreshController) {
          this.#cartRefreshController = undefined;
        }
      });
  };
}

if (!customElements.get('product-delivery-status')) {
  customElements.define('product-delivery-status', ProductDeliveryStatus);
}
